import * as React from 'react'
import * as moment from 'moment'
import { Moment } from 'moment'
import ReactHover, { Trigger, Hover } from 'react-hover'
import { Element, RentList, RentItem } from './warehouse_rent_list_types'

const DAYS_TO_RENDER_BEFORE_TERM = 1
const DAYS_TO_RENDER_AFTER_TERM = 3
const MINIMAL_DAYS_TO_RENDER = 20

// Helper class for manipulating a calendar abstraction.
export class CalendarHelper {
  private startDate: Moment
  private endDate: Moment

  constructor(startDate: string, endDate: string) {
    this.startDate = moment(startDate)
    this.endDate = moment(endDate)
  }

  // Beginning of the calendar
  start(): Moment {
    return this.startDate.clone().subtract(DAYS_TO_RENDER_BEFORE_TERM, 'day')
  }

  // End of the calendar
  end(): Moment {
    const firstDay = this.start()
    const lastDay = this.endDate.clone().add(DAYS_TO_RENDER_AFTER_TERM, 'days')

    const termLength = lastDay.diff(firstDay, 'days') + 2
    if (termLength < MINIMAL_DAYS_TO_RENDER) {
      return firstDay.add(MINIMAL_DAYS_TO_RENDER, 'days')
    }
    return lastDay
  }

  // Actual amount of days in the calendar
  totalDays(): number {
    return this.end().diff(this.start(), 'days') + 2
  }

  months(): Moment[] {
    const lastMonth = this.end().endOf('month')
    let currentMonth = this.start().startOf('month')
    const months = [currentMonth]
    while (currentMonth.isBefore(lastMonth, 'month')) {
      currentMonth = currentMonth.clone().add(1, 'month')
      months.push(currentMonth)
    }

    return months
  }

  days(): Moment[] {
    const result: Moment[] = []
    const lastDay = this.end().add(1, 'day')
    let currentDay = this.start()

    do {
      result.push(currentDay)
      currentDay = currentDay.clone().add(1, 'day')
    } while (currentDay.isBefore(lastDay))

    return result
  }

  colspanFor(date: Moment): number {
    if (this.start().month() === date.month()) {
      return date.daysInMonth() - this.start().date() + 1
    }
    return date.daysInMonth()
  }
}

interface CalendarHeadRowsProps {
  startDate: string
  endDate: string
}

export function CalendarHeadRows({ startDate, endDate }: CalendarHeadRowsProps): React.ReactElement {
  const cal = new CalendarHelper(startDate, endDate)
  return (
    <>
      <tr>
        <FirstColumns />
        {cal.months().map((m) => (
          <th key={m.format('MMM')} colSpan={cal.colspanFor(m)}>
            {m.format('MMM')}
          </th>
        ))}
      </tr>
      <tr className="rent-list-table-header-days">
        <FirstColumns />
        {cal.days().map((d) => (
          <th key={d.format('MMM-DD')}>{d.format('DD')}</th>
        ))}
      </tr>
      <tr className="rent-list-table-header-days">
        <FirstColumns />
        {cal.days().map((d) => (
          <th key={d.format('MMM-DD')}>{d.format('dd')}</th>
        ))}
      </tr>
    </>
  )
}

interface CalendarElementColumnsProps {
  element: Element
  startDate: string
  endDate: string
  rentedItems?: Record<string, RentItem[]>
  rentList?: RentList
}

export function CalendarElementColumns({
  element,
  startDate,
  endDate,
  rentedItems,
  rentList,
}: CalendarElementColumnsProps): React.ReactElement {
  const cal = new CalendarHelper(startDate, endDate)
  const { id } = element

  const cellColor = (day: Moment): string => {
    const NOT_RENTED = 'transparent'
    const RENTED_IN_CURRENT_LIST = 'green'
    const RENTED_IN_OTHER_LIST = 'lightcoral'

    if (!rentedItems) {
      return NOT_RENTED
    }

    const items = rentedItems[id]
    if (!items) {
      return NOT_RENTED
    }
    const rentListId = rentList ? rentList.id : 0
    for (const item of items) {
      const list = item.rentList
      if (!list) continue

      if (day.isBetween(list.startDate, list.endDate, 'day', '[]')) {
        return list.id === rentListId ? RENTED_IN_CURRENT_LIST : RENTED_IN_OTHER_LIST
      }
    }
    return NOT_RENTED
  }

  const eventName = (day: Moment): string | undefined => {
    if (!rentedItems) {
      return
    }

    const items = rentedItems[id]
    if (!items) {
      return
    }
    const rentListId = rentList ? rentList.id : 0
    for (const item of items) {
      const list = item.rentList
      if (!list) continue

      const end = moment(list.endDate)
      if (day.isBetween(list.startDate, end, 'day', '[]')) {
        if (list.id !== rentListId) {
          const quantity = item.quantity || 1
          return `${list.companyName || 'Unknown'} - ${list.eventName || 'No event'} (${quantity})`
        }
        return
      }
    }
  }

  return (
    <>
      {cal.days().map((d) => {
        const color = cellColor(d)
        if (color === 'transparent') {
          return <td key={d.format('YY-MM-DD')} />
        }

        const event = eventName(d)
        const colorDataAttr = color === 'green' ? 'green' : color === 'lightcoral' ? 'lightcoral' : null

        if (!event) {
          return <td key={d.format('YY-MM-DD')} className="rent-list-table-calendar-cell" data-color={colorDataAttr} />
        }

        return (
          <td
            className="rent-list-table-hoverable-cell rent-list-table-calendar-cell"
            key={d.format('YY-MM-DD')}
            data-color={colorDataAttr}
          >
            <ReactHover options={{ followCursor: true, shiftX: 20, shiftY: 0 }}>
              <Trigger type="trigger">
                <div className="rent-list-table-hoverable-cell-trigger">&nbsp;</div>
              </Trigger>
              <Hover type="hover">
                <div className="rent-list-table-hoverable-cell-hover">{event}</div>
              </Hover>
            </ReactHover>
          </td>
        )
      })}
    </>
  )
}

export function FirstColumns(): React.ReactElement {
  return <th />
}

interface WarehouseCalendarHeadRowsProps {
  startDate: string
  endDate: string
}

export function WarehouseCalendarHeadRows({ startDate, endDate }: WarehouseCalendarHeadRowsProps): React.ReactElement {
  const { I18n } = window as any
  const cal = new CalendarHelper(startDate, endDate)
  return (
    <>
      <tr>
        <th className="rent-list-table-element-header">{ I18n.t('elements.warehouse_rent_list.elements.element') }</th>
        <th className="rent-list-table-quantity-header">{ I18n.t('elements.warehouse_rent_list.elements.in_stock') }</th>
        {cal.months().map((m) => (
          <th key={m.format('MMM')} colSpan={cal.colspanFor(m)}>
            {m.format('MMM')}
          </th>
        ))}
      </tr>
      <tr className="rent-list-table-header-days">
        <th />
        <th />
        {cal.days().map((d) => (
          <th key={d.format('MMM-DD')}>{d.format('DD')}</th>
        ))}
      </tr>
      <tr className="rent-list-table-header-days">
        <th />
        <th />
        {cal.days().map((d) => (
          <th key={d.format('MMM-DD')}>{d.format('dd')}</th>
        ))}
      </tr>
    </>
  )
}
